import type { FC, SVGProps } from "react"
import { memo, useState } from "react"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { IconDotsFill18 as DotsIcon } from "nucleo-ui-fill-18"

import { cn } from "@productlane/lib/src/browser"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import { sidebarItemStyles } from "../sidebar-geometry"
import { SIDEBAR_LAYOUT_COPY as copy } from "./copy"

export interface SidebarMoreEntry {
	href: string
	title: string
	icon: FC<SVGProps<SVGSVGElement>>
	highlightFor?: string[]
}

/** Holds the items set to "Don't show" (or badged without a count), so none of them becomes unreachable. */
export const SidebarMoreItem = memo(
	({
		items,
		collapsed,
		onNavClick,
	}: {
		items: SidebarMoreEntry[]
		collapsed: boolean
		onNavClick?: () => void
	}) => {
		const [open, setOpen] = useState(false)
		const navigate = useNavigate()
		const { pathname } = useLocation()
		const isActive = items.some((item) =>
			[item.href, ...(item.highlightFor ?? [])].some((prefix) =>
				pathname.startsWith(prefix),
			),
		)

		const trigger = (
			<button
				type="button"
				aria-label={copy.more}
				data-testid="sidebar-more-item"
				className={cn(
					sidebarItemStyles(collapsed),
					"cursor-default",
					isActive || open
						? collapsed
							? "bg-gray-200 dark:bg-gray-800"
							: "bg-gray-150 dark:bg-gray-850"
						: "bg-transparent hover:bg-gray-150 hover:dark:bg-gray-800",
					!collapsed && "w-full",
				)}
			>
				<DotsIcon
					className={cn(
						"flex h-4 w-4 flex-none shrink-0 items-center justify-center text-secondary",
						!collapsed && "mr-3",
					)}
				/>
				{!collapsed && (
					<span className="min-w-0 flex-1 select-none truncate text-left text-sm font-medium tracking-tight text-secondary">
						{copy.more}
					</span>
				)}
			</button>
		)

		return (
			<DropdownMenu
				trigger={trigger}
				open={open}
				onOpenChange={setOpen}
				contentProps={{
					side: collapsed ? "right" : "bottom",
					align: "start",
					className: "w-56 z-50",
				}}
			>
				<DropdownMenuGroup>
					{items.map((item) => (
						<DropdownMenuItem
							key={item.href}
							Icon={item.icon}
							onSelect={() => {
								onNavClick?.()
								void navigate({ href: item.href })
							}}
						>
							{item.title}
						</DropdownMenuItem>
					))}
				</DropdownMenuGroup>
			</DropdownMenu>
		)
	},
)
